import { useMemo, useState, type ChangeEvent, type ReactNode } from 'react'
import { buildAssessmentPacket } from '../core/assessmentPacket'
import { queueAssessmentAction } from '../core/assessmentAgenda'
import {
  ASSESSMENT_LIMITS,
  assessmentIsCurrent,
  assessmentNodeIds,
  assessmentMarkdown,
  type AssessmentAction,
  type AssessmentAttachment,
  type AssessmentAttempt,
  type AssessmentMode,
} from '../core/runAssessment'
import { fmtUsd } from '../core/models'
import { resolveFor, useStore } from '../core/store'
import { STATUS_WORD } from '../core/vocabulary'
import { calendarMinute } from '../core/dates'
import { assessmentEstimate, reviewRun } from '../engine/runAssessment'
import { Icon } from './components/Icon'
import './RunAssessmentPanel.css'

/**
 * A disclosure's head in the pane's own style: the card's fold (`Fold` in
 * NodeCard.tsx) — its chevron, its line, its label — on a native <summary>,
 * which keeps the open state and the keyboard with the element. The browser's
 * filled triangle was the one disclosure in the Runs tab that looked like no
 * other (global.css, beside `.cfold`).
 */
export function Summary({ children }: { children: ReactNode }) {
  return (
    <summary className="cfold-head">
      <Icon name="chevron" size={9} className="cfold-chev" />
      <span className="cfold-label">{children}</span>
    </summary>
  )
}

function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** The run account stays factual. This separate, explicit press purchases a
 * fallible research assessment whose evidence and next task remain inspectable. */
export function RunAssessmentPanel({ runId }: { runId: string }) {
  const session = useStore((s) => s.session)
  const busy = useStore((s) => s.busy)
  const liveRun = useStore((s) => s.run)
  // Subscribed field by field, as Grounding's AuditButton is (D335). A bare
  // `useStore()` re-rendered every panel — one per finished run in the Runs tab —
  // on every store write: each thinking delta, each toast, each pane toggle.
  // The resolution itself is memoised rather than selected: `resolveFor` builds
  // a fresh object once a key is set, and a selector that builds its answer
  // never matches its last, which loops the page blank (D328).
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const customDialect = useStore((s) => s.customDialect)
  const model = useMemo(
    () =>
      resolveFor({ modelId, roleModels, keys, customBase, customModel, customDialect }, 'panel'),
    [modelId, roleModels, keys, customBase, customModel, customDialect],
  )
  const [mode, setMode] = useState<AssessmentMode>('research')
  const [focusNodeId, setFocusNodeId] = useState('')
  const [attachments, setAttachments] = useState<AssessmentAttachment[]>([])
  const [issue, setIssue] = useState<string | null>(null)
  const preview = useMemo(() => {
    try {
      return {
        packet: buildAssessmentPacket(session, runId, mode, attachments, focusNodeId || undefined),
        issue: null,
      }
    } catch (error) {
      return {
        packet: null,
        issue: error instanceof Error ? error.message : 'Cannot prepare this dossier.',
      }
    }
  }, [session, runId, mode, attachments, focusNodeId])
  const attempts = (session.research?.assessments?.attempts ?? []).filter(
    (a) => a.packet.runId === runId,
  )
  const estimate = model && preview.packet ? assessmentEstimate(model.entry, preview.packet) : null
  const attempted = attempts.some((a) => a.packet.revision === preview.packet?.revision)
  const disabled =
    busy ||
    (liveRun !== null && liveRun.phase !== 'ended') ||
    !model ||
    estimate === null ||
    !preview.packet ||
    attempted
  const changeAttachment = (id: string, change: Partial<AssessmentAttachment>) =>
    setAttachments((all) => all.map((a) => (a.id === id ? { ...a, ...change } : a)))
  async function addFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    try {
      if (files.length + attachments.length > ASSESSMENT_LIMITS.attachments)
        throw new Error(`Select at most ${ASSESSMENT_LIMITS.attachments} text attachments.`)
      const added: AssessmentAttachment[] = []
      for (const file of files) {
        if (file.size > ASSESSMENT_LIMITS.attachmentBytes)
          throw new Error(
            `${file.name} exceeds ${ASSESSMENT_LIMITS.attachmentBytes} bytes. Select a focused public artifact.`,
          )
        if (!/\.(txt|md|json|py|js|ts|cnf|csv|lean)$/i.test(file.name))
          throw new Error(
            'Attach text, code or JSON. Add PDF excerpts through the existing evidence workspace.',
          )
        const content = await file.text()
        if (!content.trim() || content.includes('\u0000'))
          throw new Error(`${file.name} is not a readable text attachment.`)
        added.push({
          id: crypto.randomUUID(),
          title: file.name,
          kind: 'argument',
          provenance: 'user-supplied',
          content,
        })
      }
      setAttachments((current) => [...current, ...added])
      setIssue(null)
    } catch (error) {
      setIssue(error instanceof Error ? error.message : 'Cannot read this file.')
    }
  }
  async function run() {
    if (!model || disabled) return
    setIssue(null)
    try {
      await reviewRun(
        session.id,
        runId,
        model.entry.id,
        mode,
        attachments,
        focusNodeId || undefined,
      )
    } catch (error) {
      setIssue(
        error instanceof Error ? error.message : 'Review failed; inspect its retained receipt.',
      )
    }
  }
  function queue(attempt: AssessmentAttempt, action: AssessmentAction) {
    try {
      const s = useStore.getState()
      const agenda = queueAssessmentAction(s.session, attempt, action, Date.now())
      s.setResearchState({ ...s.session.research!, agenda })
      s.setResearchOpen(true, action.action === 'experiment' ? 'mathematics' : 'findings')
      setIssue(null)
    } catch (error) {
      setIssue(error instanceof Error ? error.message : 'Cannot queue this action.')
    }
  }
  return (
    <section className="run-assessment" aria-label="Research assessment">
      <h3>Review this run</h3>
      <p>Find the strongest supported result, compare prior work, and prepare the next question.</p>
      <p className="run-assessment__note">
        AI assessments remain proposals. Source checks, proof reviews and exact certificates retain
        their own evidence requirements.
      </p>
      <details>
        <Summary>Prepare the review dossier</Summary>
        <label>
          Claims to review
          <select
            aria-label="Claims to review"
            value={focusNodeId}
            onChange={(e) => setFocusNodeId(e.target.value)}
            disabled={busy}
          >
            <option value="">Whole run with its premises</option>
            {assessmentNodeIds(session, runId).map((id) => (
              <option key={id} value={id}>
                {session.nodes[id]!.label} and its premises
              </option>
            ))}
          </select>
        </label>
        <label>
          Review focus
          <select
            aria-label="Review focus"
            value={mode}
            onChange={(e) => setMode(e.target.value as AssessmentMode)}
            disabled={busy}
          >
            <option value="research">Research contribution and next question</option>
            <option value="originality">Originality and exact prior-work comparison</option>
          </select>
        </label>
        <p>
          Includes complete retained claims and premises, theorem hypotheses, available sources and
          public experiment results. Private evaluator material is excluded. Add exact arguments,
          checker output or source excerpts when needed.
        </p>
        <label>
          Attach public text or code
          <input
            type="file"
            multiple
            accept=".txt,.md,.json,.py,.js,.ts,.cnf,.csv,.lean"
            disabled={busy}
            onChange={addFiles}
          />
        </label>
        {attachments.map((a) => (
          <fieldset key={a.id}>
            <legend>{a.title}</legend>
            <label>
              Content type
              <select
                value={a.kind}
                onChange={(e) =>
                  changeAttachment(a.id, { kind: e.target.value as AssessmentAttachment['kind'] })
                }
              >
                <option value="argument">Argument / definition</option>
                <option value="checker">Checker source</option>
                <option value="certificate">Certificate</option>
                <option value="source-text">Prior source text</option>
                <option value="observation">Observed result</option>
              </select>
            </label>
            <label>
              Declared provenance
              <select
                value={a.provenance}
                onChange={(e) =>
                  changeAttachment(a.id, {
                    provenance: e.target.value as AssessmentAttachment['provenance'],
                  })
                }
              >
                <option value="user-supplied">User supplied</option>
                <option value="ai-authored">AI authored</option>
                <option value="local-execution">Local execution report</option>
              </select>
            </label>
            <details>
              <Summary>Inspect full text</Summary>
              <pre>{a.content}</pre>
            </details>
            <button
              type="button"
              onClick={() => setAttachments((all) => all.filter((x) => x.id !== a.id))}
            >
              Remove attachment
            </button>
          </fieldset>
        ))}
        {preview.packet && (
          <p>
            {preview.packet.nodeIds.length} claims · {preview.packet.references.length} evidence
            references
          </p>
        )}
        {preview.packet?.missingContext.map((gap) => (
          <p className="run-assessment__note" key={gap}>
            {gap}
          </p>
        ))}
        <button
          type="button"
          disabled={!preview.packet}
          onClick={() =>
            preview.packet &&
            download(`run-${runId}-dossier.json`, JSON.stringify(preview.packet, null, 2))
          }
        >
          Export exact dossier
        </button>
      </details>
      <div className="run-assessment__actions">
        <button type="button" disabled={disabled} onClick={run}>
          {busy ? 'A pass is running' : 'Review this run'}
        </button>
        <span>
          {model
            ? `${model.entry.name} · ${estimate === null ? 'estimate unavailable' : `${fmtUsd(estimate)} estimated allowance`}`
            : 'Connect the model routed to the panel role to review.'}
        </span>
      </div>
      <p className="run-assessment__note">
        One bounded model request, with no automatic follow-up. Queueing a proposed action is free;
        the agenda’s run control authorizes execution.
      </p>
      {attempted && (
        <p>
          This exact dossier has an attempt below. Add new evidence for a separate review;
          interrupted calls are never replayed.
        </p>
      )}
      {(issue || preview.issue) && <p role="alert">{issue || preview.issue}</p>}
      {[...attempts].reverse().map((attempt) => {
        const current = assessmentIsCurrent(session, attempt)
        return (
          <article key={attempt.id} className="run-assessment__report">
            <h4>
              {attempt.packet.mode === 'originality' ? 'Originality review' : 'Research review'} ·{' '}
              {attempt.state}
            </h4>
            <p>
              {attempt.model.name} · {calendarMinute(attempt.createdAt)} ·{' '}
              {current ? 'Inputs unchanged' : 'Inputs changed since this review'}
            </p>
            {attempt.issue && <p role="status">{attempt.issue}</p>}
            {attempt.state === 'pending' && !busy && (
              <p>
                There is no retained terminal result. Inspect the exact attempt before further paid
                work.
              </p>
            )}
            {attempt.result?.findings.map((f) => (
              <div key={f.id}>
                <h4>{f.statement}</h4>
                <dl>
                  <dt>Correctness · AI assessment: {f.correctness.status}</dt>
                  <dd>{f.correctness.explanation}</dd>
                  <dt>Originality · AI assessment: {f.originality.status}</dt>
                  <dd>{f.originality.explanation}</dd>
                  <dt>Significance</dt>
                  <dd>{f.significance}</dd>
                  <dt>Usefulness</dt>
                  <dd>{f.usefulness}</dd>
                  <dt>Scope</dt>
                  <dd>{f.scope}</dd>
                  <dt>Limitations</dt>
                  <dd>
                    {f.limitations.join(' ') || 'None supplied; completeness is not certified.'}
                  </dd>
                </dl>
                <p>
                  Evidence: {f.evidenceIds.join(', ') || 'No separate evidence reference supplied.'}
                </p>
                {f.nodeIds.map((id) => (
                  <button type="button" key={id} onClick={() => useStore.getState().select(id)}>
                    Open {session.nodes[id]?.label || id}
                  </button>
                ))}
              </div>
            ))}
            {attempt.result?.comparisons.map((c, i) => (
              <details key={i}>
                <Summary>
                  Proposed comparison: {c.theoremLocator} · {c.relationship}
                </Summary>
                <p>Source reference: {c.referenceId}</p>
                {c.mappings.map((m) => (
                  <dl key={m.dimension}>
                    <dt>{m.dimension}</dt>
                    <dd>Ours: {m.ours}</dd>
                    <dd>Prior: {m.prior}</dd>
                    <dd>Correspondence: {m.correspondence}</dd>
                    <dd>Gap: {m.gap}</dd>
                  </dl>
                ))}
                <p>Translation obligation: {c.translationObligation}</p>
                <p>Unresolved: {c.unresolved.join(' ')}</p>
              </details>
            ))}
            {attempt.result?.lessons.map((lesson) => (
              <details key={lesson.id}>
                <Summary>Retained negative finding: {lesson.failedAssumption}</Summary>
                <p>{lesson.scope}</p>
                <p>Evidence: {lesson.evidenceIds.join(', ')}</p>
                <p>Every proposed repair must address: {lesson.repairRequirements.join(' ')}</p>
              </details>
            ))}
            {attempt.result?.next.map((action) => {
              const queued = session.research?.agenda?.steps.some(
                (s) =>
                  s.assessment?.assessmentId === attempt.id && s.assessment.actionId === action.id,
              )
              return (
                <div key={action.id} className="run-assessment__next">
                  <h4>{action.question}</h4>
                  <p>{action.purpose}</p>
                  <dl>
                    <dt>If supporting</dt>
                    <dd>{action.outcomes.supporting}</dd>
                    <dt>If challenging</dt>
                    <dd>{action.outcomes.challenging}</dd>
                    <dt>If inconclusive</dt>
                    <dd>{action.outcomes.inconclusive}</dd>
                  </dl>
                  <button
                    type="button"
                    disabled={busy || !current || queued}
                    onClick={() => queue(attempt, action)}
                  >
                    {queued
                      ? 'Queued in the agenda'
                      : `Queue ${action.action} · ${STATUS_WORD.free.toLowerCase()}`}
                  </button>
                  {action.action === 'experiment' && (
                    <p>
                      Prepare and bind an exact experiment plan in Mathematics before execution.
                    </p>
                  )}
                </div>
              )
            })}
            {attempt.result?.missingContext.map((gap) => (
              <p key={gap}>Missing context: {gap}</p>
            ))}
            <details>
              <Summary>Exact evidence and attempt</Summary>
              {attempt.response && (
                <details>
                  <Summary>Returned structured response</Summary>
                  {attempt.response.content === null ? (
                    <p>
                      The {attempt.response.bytes}-byte response exceeded the retention allowance;
                      its text was not retained. Fingerprint: {attempt.response.fingerprint}.
                    </p>
                  ) : (
                    <pre>{attempt.response.content}</pre>
                  )}
                </details>
              )}
              <p>
                Attempt {attempt.id} · operation {attempt.opId} · dossier revision{' '}
                {attempt.packet.revision}
              </p>
              {attempt.packet.references.map((ref) => (
                <details key={ref.id}>
                  <Summary>
                    {ref.label} · {ref.id}
                  </Summary>
                  <p>{ref.provenance}</p>
                  <pre>{ref.content}</pre>
                </details>
              ))}
              <details>
                <Summary>Full research context</Summary>
                <pre>{attempt.packet.context}</pre>
              </details>
              <pre>
                {JSON.stringify(
                  session.ops.find((o) => o.id === attempt.opId)?.modelCalls ?? [],
                  null,
                  2,
                )}
              </pre>
            </details>
            <div className="run-assessment__actions">
              <button
                type="button"
                onClick={() =>
                  download(`${attempt.id.replaceAll(':', '-')}.md`, assessmentMarkdown(attempt))
                }
              >
                Export review and evidence
              </button>
              <button
                type="button"
                onClick={() =>
                  download(
                    `${attempt.id.replaceAll(':', '-')}.json`,
                    JSON.stringify(
                      { attempt, operation: session.ops.find((o) => o.id === attempt.opId) },
                      null,
                      2,
                    ),
                  )
                }
              >
                Export exact JSON
              </button>
              <button
                type="button"
                onClick={() => useStore.getState().setResearchOpen(true, 'mathematics')}
              >
                Review proof obligations
              </button>
            </div>
          </article>
        )
      })}
    </section>
  )
}
